<template>
  <div id="express-checkout-modal" class="modal fade" tabindex="-1" role="dialog"
    aria-labelledby="express-checkout-modal-label">
    <div class="modal-dialog modal-lg" role="document">
      <transition name="fade" mode="out-in">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click.prevent="modalReset">
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 id="expressCheckoutModalLabel" class="modal-title" v-if="canPlaceOrder">
              Express Checkout for {{ orderItemsCount }} Item(s): ${{ orderTotalAmount }}
            </h4>
            <h4 v-else-if="hasError.error">Ugh Oh!</h4>
          </div>
          <div class="modal-body">
            <div class="alert alert-danger" role="alert"
              v-if="!canPlaceOrder && hasError.error && hasError.errorType === 'shippingError'">
              There was an issue preparing this checkout session. Please reload the page and try again or contact sales
              for further assistance. Thank you for your patience.
            </div>
            <div v-else-if="hasError.error && hasError.errorType === 'shippingError'">
              <p class="label label-warning">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>&nbsp;
                You must have a minium of one saved shipping address!
              </p>
              <p>
                You will need to save a shipping address to your account&nbsp;
                <a href="/my-account">here&nbsp;<i class="fa fa-external-link" aria-hidden="true"></i></a>
              </p>
              <p>Alternatively, If you do not wish to save a shipping address then please use the regular checkout option
                from your cart.</p>
            </div>
            <div v-if="hasError.error && hasError.errorType === 'orderError'">
              <p class="label label-warning">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>&nbsp;
                There was a problem processing your order!
              </p>
              <p>Reasons this could occur:</p>
              <ul>
                <li>You don't have any saved cards on your account.</li>
                <li>The card on file is inactive/expired.</li>
                <li>The CVV was entered incorrectly.</li>
                <li>Our credit card processor is experiencing technical difficulties.</li>
              </ul>
              <p>You can try reloading the page to try again, use our regular checkout option, or contact one of our
                service members: 1-866-774-7628. Thank you.</p>
            </div>
            <div class="text-center" v-show="orderProcessing">
              <h3>Processing...</h3>
            </div>
            <div v-if="!orderProcessing && canPlaceOrder && !hasError.error">
              <section id="disclaimer">
                <div class="panel panel-warning">
                  <div class="panel-heading">
                    <h4><i class="fa fa-bell-o" aria-hidden="true"></i>&nbsp;NOTICE</h4>
                  </div>
                  <div class="panel-body">
                    <ul class="col-sm-12">
                      <li>Placing an order via "Express Checkout" will load your previous/saved address and billing
                        information.
                      </li>
                      <li>Shipping defaults to our quickest shipping option.</li>
                      <br>
                    </ul>
                    <div class="text-center">
                      <p class="label label-danger">
                        If you need to customize your checkout information - please select the regular checkout option
                        from your cart.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              <section id="shipping-address">
                <div class="panel panel-default">
                  <div class="panel-heading">
                    <h4><i class="fa fa-check-square-o" aria-hidden="true"></i>&nbsp;Shipping Address</h4>
                  </div>
                  <div class="panel-body">
                    <p>{{ shippingAddressData.formatted_full }}</p>
                  </div>
                </div>
              </section>
              <section id="billing-address" v-if="!userIsOnTerms">
                <div class="panel panel-default">
                  <div class="panel-heading">
                    <h4><i class="fa fa-check-square-o" aria-hidden="true"></i>&nbsp;Billing Address</h4>
                  </div>
                  <div class="panel-body">
                    <p>{{ paymentData.billing_address.formatted_full }}</p>
                  </div>
                </div>
              </section>
              <section id="shipping">
                <div class="panel panel-default">
                  <div class="panel-heading">
                    <h4><i class="fa fa-check-square-o" aria-hidden="true"></i>&nbsp;Shipping</h4>
                  </div>
                  <div class="panel-body">
                    <div v-if="hasRegularShipping">
                      Order: {{ shippingData.result[0].name }} (${{ shippingData.result[0].rate.amount }})
                    </div>
                    <div v-if="hasSwatchShipping">
                      Swatch: {{ shippingData.swatch_order_result[0].name }} (${{
                        shippingData.swatch_order_result[0].rate.amount
                      }})
                    </div>
                  </div>
                </div>
              </section>
              <section id="payment">
                <div class="panel panel-default">
                  <div class="panel-heading">
                    <h4><i class="fa fa-check-square-o" aria-hidden="true"></i>&nbsp;Payment</h4>
                  </div>
                  <div class="panel-body">
                    <div v-if="userIsOnTerms">
                      <p>As a terms customer, payment for your order will be processed and paid based on your payment
                        terms with NIC.</p>
                      <p>Please contact us if you have any questions/concerns.</p>
                    </div>
                    <div v-else-if="userOrdersFromDistributor">
                      <p>Orders are processed and distributed by the local distributor in your region. They are a
                        verified partner of {{ $root.domainName }}.</p>
                      <p>Your order and shipping details will be transferred your local distributor. You will be
                        contacted within one business day to complete the order process. Thank you.</p>
                    </div>
                    <div v-else>
                      <p>Visa Ending in {{ paymentData.masked_number }}</p>
                      <label>Security Code (CVV)</label>
                      <input class="form-control" :class="cvvValue && cvvValue.length >= 3 ? '' : 'has-error-glow'"
                        type="text" name="cc-csc" autocomplete="cc-csc" placeholder="ENTER CVV (REQUIRED)" :maxlength="4"
                        :minlength="3" v-model="cvvValue" @input="cvvValue = formatCVV($event.target.value)">
                      <div class="error-text error-bottom" v-if="avsFailure">
                        Your billing address could not be verified. Please update your billing address and try again.
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section id="promo">
                <div class="panel panel-default">
                  <div class="panel-heading">
                    <h4><i class="fa fa-check-square-o" aria-hidden="true"></i>&nbsp;Promo Code (Optional)</h4>
                  </div>
                  <div class="panel-body">
                    <div class="form-group">
                      <input type="text" name="" v-model="storeCode" class="form-control coupon-code">
                    </div>
                    <div v-if="formErrors.length">
                      <div class="col-sm-12 error-text" v-for="formError in formErrors" :key="formError.index">
                        {{ formError }}
                      </div>
                    </div>
                    <div v-else>
                      <div class="col-sm-12 success-text" v-for="success in formSuccess" :key="success.index">
                        {{ success }}
                      </div>
                    </div>
                    <div class="form-group">
                      <button class="btn btn-blue btn-shadow btn-block" @click.prevent="addCoupon" :disabled="loading">
                        Add Coupon
                        <i class="fa fa-spin fa-spinner" v-if="loading"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </section>
              <section id="order-summary">
                <div class="panel panel-default">
                  <div class="panel-heading">
                    <h4><i class="fa fa-check-square-o" aria-hidden="true"></i>&nbsp;Order Total</h4>
                  </div>
                  <div class="panel-body">
                    <div>Items: ${{ expressCheckoutData.items_total_before_discount.amount }}</div>
                    <div>Shipping: ${{ shippingAmount }}</div>
                    <div>Tax: ${{ taxAmount }}</div>
                    <div>Savings: ${{ savingsAmount }}</div>
                    <div>---</div>
                    <div>Total for {{ orderItemsCount }} Item(s): ${{ orderTotalAmount }}</div>
                  </div>
                </div>
              </section>
              <section id="order-agreement">
                <div class="panel panel-info">
                  <div class="panel-heading">
                    <h4><i class="fa fa-thumbs-o-up" aria-hidden="true"></i>&nbsp;By clicking "Place Order"</h4>
                  </div>
                  <div class="panel-body">
                    <p>You agree and accept all of {{ $root.domainName }}' shipping, payment, and order terms.
                      <a target="_blank" href="/terms-and-policies">
                        Terms and Conditions&nbsp;<i class="fa fa-external-link" aria-hidden="true"></i>
                      </a>
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
          <div class="modal-footer row" v-show="canPlaceOrder && !orderProcessing && !hasError.error">
            <section id="submit">
              <div class="col-md-6 text-center">
                <button class="btn btn-block btn-black" type="button" data-dismiss="modal" :disabled="orderProcessing"
                  @click.prevent="modalReset">
                  Cancel
                </button>
              </div>
              <div class="col-md-6 text-center">
                <button class="btn btn-block btn-light-green" type="button"
                  @click.prevent="placeOrder(expressCheckoutData.id)" :disabled="!meetsOrderReqs || orderProcessing">
                  <i class="fa fa-spin fa-spinner" v-show="orderProcessing"></i>&nbsp;Place Order
                </button>
              </div>
            </section>
          </div>
        </div>
      </transition>
      <div id="loading-express-checkout" v-show="!canPlaceOrder && !hasError.error">
        <div class="loading-contents">
          <Spinner color="#3982c4" />
          <h4>Preparing Express Checkout...</h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
  * @name ExpressCheckoutModal.vue
  * @file: displays a brief summary of address/shipping/payment info and handles the user actions to place an order.
  * @external: ShoppingCart.vue
  * @alias: Express Checkout Modal
  */
import checkoutService from '../../api/checkoutAPI';
import Spinner from '../Spinner.vue';
import formatting from '../../mixins/formatting';
import helpers from '../../mixins/helpers';

export default {
  name: 'express-checkout-modal',

  components: {
    Spinner
  },

  mixins: [formatting, helpers],

  data() {
    return {
      orderProcessing: false,
      canPlaceOrder: false,
      paymentData: {},
      shippingData: {},
      shippingAddressData: {},
      hasRegularShipping: false,
      hasSwatchShipping: false,
      orderItemsCount: 0,
      orderTotalAmount: '0.00',
      shippingAmount: '0.00',
      taxAmount: '0.00',
      savingsAmount: '0.00',
      cvvValue: '',
      displayModalContent: false,
      userOrdersFromDistributor: false,
      orderProcessingError: false,
      hasError: {
        error: false,
        errorType: ''
      },
      avsFailure: false,
      storeCode: '',
      loading: false,
      formErrors: [],
      formSuccess: []
    };
  },

  props: {
    expressCheckoutData: Object
  },

  computed: {
    meetsOrderReqs() {
      return (
        this.canPlaceOrder &&
        this.cvvValue !== '' &&
        this.paymentData &&
        this.shippingData &&
        (this.hasRegularShipping || this.hasSwatchShipping) ||
        this.userIsOnTerms ||
        this.userOrdersFromDistributor
      );
    },

    userIsOnTerms() {
      return this.$root.me.terms === 'wire' || this.$root.me.terms === 'NET 30';
    },

    checkoutId() {
      if (Object.values(this.expressCheckoutData).length > 0) {
        return this.expressCheckoutData.id;
      }

      return false;
    }
  },

  watch: {
    // this will begin the api/checkout process once the expressCheckoutData prop has data.
    async expressCheckoutData() {
      if (Object.values(this.expressCheckoutData).length > 0) {
        await this.getShippingAddress();

        if (!this.hasError.error) {
          await this.prepShippingOptions(this.checkoutId);
          await this.getShippingOptions(this.checkoutId);

          if (this.hasError.error) return;

          await this.setShippingOptions(this.checkoutId);

          if (!this.userIsOnTerms || !this.userOrdersFromDistributor) {
            await this.getPaymentOption();
          }

          this.addGATracking();
          this.canPlaceOrder = true;
          this.displayModalContent = true;
        }
      }
    }
  },

  methods: {
    addGATracking() {
      let data = this.expressCheckoutData;
      let items = [];

      // this is for GA4
      data.order_items.forEach(function (item, index) {
        items.push({
          item_id: item.sku,
          item_name: item.name,
          coupon: undefined,
          currency: "USD",
          discount: undefined,
          index: index,
          item_brand: "Prismatic",
          item_category: item.product.type,
          item_list_id: undefined,
          item_list_name: undefined,
          item_variant: undefined,
          location_id: "Express Checkout",
          price: parseFloat(item.price.amount),
          quantity: parseFloat(item.quantity.value)
        });
      });

      // this is for GA4
      dataLayer.push({
        event: "begin_checkout",
        ecommerce: {
          currency: "USD",
          value: parseFloat(data.items_total.amount),
          items
        }
      });
    },

    /**
     * @function getShippingAddress
     * @summary fetches the users first (last used/saved) address from the api and sets the data to shippingAddressData.
     * @summary will also set an error flag to prevent over fetching and error chaining due to missing data.
     */
    async getShippingAddress() {
      await axios.get(`/api/proxify/me/addresses?limit=1&type=shipping&orderby=last_used_at`)
        .then(({ data: response }) => {
          if (response.data.length > 0) {
            this.shippingAddressData = response.data[0];
          } else {
            this.displayModalContent = true;
            this.setError({ error: true, errorType: 'shippingError' });
            return;
          }
        })
        .catch((error) => {
          console.error(error);
        })
    },

    /**
     * @function prepShippingOptions
     * @summary takes the shipping address from getShippingAddress and adds it to the checkout session.
     * @param checkoutId the checkout ID - given once a checkout session has begun.
     */
    async prepShippingOptions(checkoutId) {
      let address = this.shippingAddressData;

      const payload = {
        shipping_address: {
          city: address.city,
          country_code: address.country.code,
          name: {
            family_name: address.name.family_name,
            given_name: address.name.given_name,
            middle_name: address.name.middle_name
          },
          phone_number: address.phone_number,
          postal_code: address.postal_code,
          region_code: address.region.code,
          street_address: address.street_address,
          street_address_2: address.street_address_2,
          type: address.type
        }
      };

      await checkoutService.update(checkoutId, payload)
        .catch((error) => {
          console.error(error);
        })
    },

    /**
     * @function getShippingOptions
     * @summary shipping options can be fetched now that the checkout has a shipping address.
     * @param checkoutId the checkout ID - given once a checkout session has begun.
     */
    async getShippingOptions(checkoutId) {
      await checkoutService.shippingOptions(checkoutId)
        .then(({ data: response }) => {
          if (Object.values(response).length > 0) {
            this.shippingData = response;
          }
        })
        .catch((error) => {
          console.error(error);
          this.setError({ error: true, errorType: 'shippingError' });
        })
    },

    /**
     * @function setShippingOptions
     * @summary now that we have shipping options, we can update the checkout session with those selections.
     * @param checkoutId the checkout ID - given once a checkout session has begun.
     */
    async setShippingOptions(checkoutId) {
      let expressCheckoutPayload = {
        shipping_option_code: this.shippingData.result.length ? this.shippingData.result[0].code : null,
        swatch_order_shipping_option_code: this.shippingData.swatch_order_result.length ? this.shippingData.swatch_order_result[0].code : null,
      };

      if (this.shippingData.result.length) {
        this.hasRegularShipping = true;

        if (this.shippingData.result[0].code === 'local_distributor') {
          this.userOrdersFromDistributor = true;
        }
      }

      if (this.shippingData.swatch_order_result.length) {
        this.hasSwatchShipping = true;

        if (this.shippingData.swatch_order_result[0].code === 'local_distributor') {
          this.userOrdersFromDistributor = true;
        }
      }

      await checkoutService.update(checkoutId, expressCheckoutPayload)
        .then(({ data: response }) => {
          this.orderItemsCount = response.order_items.length;
          this.shippingAmount = response.shipping.amount;
          this.taxAmount = response.tax.amount;
          this.savingsAmount = response.savings.amount;
          this.orderTotalAmount = response.total.amount;

          if (response.coupon_code !== null) {
            this.showDiscounts(response)
          }
        })
        .catch((error) => {
          console.error(error);
        })
    },

    /**
     * @function getPaymentOption
     * @summary all orders will fetch the payment options except for terms/local distributor users.
     */
    async getPaymentOption() {
      await axios.get(`/api/proxify/me/payment-options?orderby=last_used_at`)
        .then(({ data: response }) => {
          if (response.data.length > 0) {
            this.paymentData = response.data[0];
          } else {
            this.setError({ error: true, errorType: 'orderError' });
          }
        })
        .catch((error) => {
          console.error(error);
        })
    },

    /**
     * @function placeOrder
     * @summary submit the order.
     * @param checkoutId the checkout ID - given once a checkout session has begun.
     */
    async placeOrder(checkoutId) {
      this.orderProcessing = true;

      let payload = {
        payment_details: {
          security_code: this.cvvValue
        },
        payment_option_id: this.paymentData.id
      };

      await checkoutService.submit(checkoutId, payload)
        .then(({ data: response }) => {
          if (response.length > 0) {
            this.handleCheckoutSubmitResponse(response[0].id);
          } else {
            this.setError({ error: true, errorType: 'orderError' });
          }
        })
        .catch((error) => {
          if (error.response.data.message.toLowerCase().includes('avs_failure')) {
            this.avsFailure = true;
            this.scrollToCenter('#payment');
          } else {
            console.error(error);
            this.setError({ error: true, errorType: 'orderError' });
          }
          this.orderProcessing = false;
        })
    },

    async addCoupon() {
      let payload = {
        coupon_code: this.storeCode,
      };

      this.formSuccess = [];
      this.formErrors = [];
      this.loading = true;

      if (this.storeCode === '' || this.storeCode === undefined) {
        this.formErrors.push("Whoa! No coupon was entered.");
        this.loading = false;
        return;
      }

      try {
        let response = await checkoutService.update(this.checkoutId, payload);

        this.handleAddCouponResponse(response);

        if (response?.data) {
          // this is for GA4
          dataLayer.push({
            event: "select_promotion",
            ecommerce: {
              creative_name: response.data.coupon_code,
              creative_slot: undefined,
              location_id: undefined,
              promotion_id: undefined,
              promotion_name: response.data.coupon_code,
              value: parseFloat(response.data.items_total.amount),
              items: [{
                item_id: undefined,
                item_name: undefined,
                affiliation: this.$root.me.customer_id ? this.$root.me.customer_id : undefined,
                coupon: response.data.coupon_code,
                currency: "USD",
                discount: parseFloat(response.data.savings.amount),
                index: undefined,
                item_brand: "Prismatic",
                item_category: undefined,
                item_list_id: undefined,
                item_list_name: undefined,
                item_variant: undefined,
                location_id: "Express Checkout",
                price: undefined,
                quantity: 1
              }]
            }
          });
        }

      } catch (error) {
        console.error(error);
        const apiMessage = error.response.data.message;
        this.loading = false;
        this.formErrors.push(apiMessage);
      }
    },

    handleAddCouponResponse(response) {
      if (!response.data.savings.amount) {
        this.formErrors.push("Order items don't meet requirements to use this coupon.");
        return;
      }

      this.showDiscounts(response.data);
      this.formErrors = [];
      this.loading = false;
    },

    showDiscounts(order) {
      this.formSuccess.push(`The Coupon "${order.coupon_code}" has been added.`);
      this.savingsAmount = order.savings.amount;
      this.orderTotalAmount = order.total.amount;
    },

    /**
     * @function handleCheckoutSubmitResponse
     * @summary upon a successful placeOrder response we redirect the user to the order history page.
     */
    handleCheckoutSubmitResponse(id) {
      this.$cookie.set('confirm', 'y');
      window.location = '/order-history/' + id;
    },

    setError(item) {
      this.hasError.error = item.error;
      this.hasError.errorType = item.errorType;
    },

    // on a close/cancel we reset some of the local Vue state.
    modalReset() {
      $('#express-checkout-modal').modal('hide');
      this.orderProcessing = false;
      this.canPlaceOrder = false;
      this.hasRegularShipping = false;
      this.hasSwatchShipping = false;
      this.setError({ error: false, errorType: '' });
      this.cvvValue = '';
      this.displayModalContent = false;
      this.userOrdersFromDistributor = false;
      this.orderProcessingError = false;
      this.storeCode = '';
      this.loading = false;
      this.formErrors = [];
      this.formSuccess = [];
    },
  },
};
</script>

<style scoped lang="scss">
#express-checkout-modal {

  .modal-dialog {
    background-color: #fff;
    border-radius: 6px;
    overflow: auto;

    #loading-express-checkout {
      text-align: center;
      position: relative;
      height: 25vh;
      z-index: 0;
      background-color: #e1e1e1;

      .loading-contents {
        margin-bottom: 0 !important;
      }
    }

    .modal-content {
      background-color: #fff;
      color: #333;
      border-radius: 6px;
      margin-bottom: 0;

      .modal-header {
        border-bottom: 1px solid #e5e5e5;
        padding: inherit;

        .close {
          color: #333;
          font-size: 25px;
          padding: 0;
        }
      }

      .modal-body {
        padding-bottom: 0;

        section {
          margin-bottom: 0.8rem;
        }

        #disclaimer {
          .label {
            white-space: initial;
          }
        }

        #promo {
          .coupon-code {
            margin-bottom: 10px;
          }

          .error-text {
            display: flex;
            color: #da4f49;
            background-color: #da4f492e;
            border-radius: 6px;
            border: 1px solid #da4f49;
            margin-bottom: 10px;
            padding: 5px;
            font-weight: 700;
          }

          .success-text {
            display: flex;
            color: #3c763d;
            background-color: #dff0d8;
            border-radius: 6px;
            border: 1px solid #6b8162;
            margin-bottom: 10px;
            padding: 5px;
          }
        }

        .input-label {
          font-size: 1.1rem;
          text-transform: uppercase;
          color: #777;
        }

        .form-group {
          margin-bottom: 0;
        }
      }

      .modal-footer {
        text-align: left;

        #submit {
          .btn-light-green {
            display: block;
            width: 100%;
            font-size: inherit;
          }
        }
      }
    }
  }
}

@media (max-width: 900px) {
  #express-checkout-modal {
    .modal-dialog {
      #loading-express-checkout {
        height: 100vh;
      }

      .modal-content {
        .modal-body {
          #disclaimer {
            .label {
              display: block;
            }
          }
        }
      }
    }
  }
}
</style>
